import React, { useState, useEffect } from 'react';
import { ChevronDown, Menu, X, Instagram, Linkedin, Twitter } from 'lucide-react';
import { navigationItems, footerData } from '../data/mockData';
import { Link, useLocation } from 'react-router-dom';
import logoWhite from '../assets/logos/logo-white.png';
import logoBlack from '../assets/logos/logo-black.png';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTheme, setActiveTheme] = useState('dark');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const observerOptions = {
      root: null,
      rootMargin: '-80px 0px -80% 0px',
      threshold: 0
    };

    const handleIntersect = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const theme = entry.target.getAttribute('data-theme');
          if (theme) setActiveTheme(theme);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    const sections = document.querySelectorAll('section[data-theme]');
    sections.forEach(section => observer.observe(section));

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      sections.forEach(section => observer.unobserve(section));
    };
  }, []);

  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isLight = activeTheme === 'light';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 border-b ${isScrolled
        ? isLight
          ? 'bg-white/40 backdrop-blur-2xl border-gray-200/30 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.03)]'
          : 'bg-[#0d0d10]/40 backdrop-blur-2xl border-white/5 py-3 sm:py-4 shadow-[0_8px_32px_rgba(0,0,0,0.1)]'
        : 'bg-transparent border-transparent py-4 sm:py-6'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            onClick={(e) => {
              if (isHomePage) {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            className="flex items-center group cursor-pointer h-10 w-40 overflow-hidden relative"
          >
            <img
              src={isLight ? logoBlack : logoWhite}
              alt="ZenCap Logo"
              className="h-24 w-auto object-contain absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 scale-150 transition-transform duration-500 group-hover:scale-[1.6]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-12">
            {navigationItems.map((item, index) => {
              const Tag = isHomePage ? 'a' : Link;
              const props = isHomePage ? { href: item.href } : { to: `/${item.href}` };

              return (
                <Tag
                  key={index}
                  {...props}
                  className={`relative group/link text-[15px] font-bold tracking-tight transition-all ${isLight ? 'text-[#0d0d10]/70 hover:text-[#3bd07a]' : 'text-white/70 hover:text-white'
                    }`}
                >
                  {item.label}
                  <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-[#3bd07a] transition-all duration-300 group-hover/link:w-full`} />
                </Tag>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 rounded-xl transition-colors ${isLight ? 'text-[#0d0d10] bg-gray-100' : 'text-white bg-white/10'
              }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className={`lg:hidden absolute top-full left-0 right-0 animate-in fade-in slide-in-from-top-2 duration-300 border-t shadow-2xl ${isLight ? 'bg-white/95 backdrop-blur-xl border-gray-100' : 'bg-[#0d0d10]/95 backdrop-blur-xl border-white/5'
            }`}>
            <div className="p-6 space-y-4">
              {navigationItems.map((item, index) => {
                const Tag = isHomePage ? 'a' : Link;
                const props = isHomePage ? { href: item.href } : { to: `/${item.href}` };

                return (
                  <Tag
                    key={index}
                    {...props}
                    className={`block text-lg font-bold transition-all ${isLight
                      ? 'text-[#0d0d10]/70 hover:text-[#3bd07a]'
                      : 'text-white/80 hover:text-white'
                      }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Tag>
                );
              })}

              {/* Mobile Social Links */}
              <div className="pt-6 border-t border-white/5">
                <span className={`block text-xs font-bold uppercase tracking-widest mb-4 ${isLight ? 'text-[#0d0d10]/40' : 'text-white/40'}`}>
                  Follow us
                </span>
                <div className="flex gap-4">
                  <a
                    href={footerData.socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${isLight ? 'bg-gray-100 text-[#0d0d10]' : 'bg-white/5 text-white'}`}
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/xalyte/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${isLight ? 'bg-gray-100 text-[#0d0d10]' : 'bg-white/5 text-white'}`}
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a
                    href="https://x.com/ZenCapOfficial"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${isLight ? 'bg-gray-100 text-[#0d0d10]' : 'bg-white/5 text-white'}`}
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;